import { api } from "@/api/axios.ts";
import { ApiIdObject, ApiOperationResult } from "@/types/common.types.ts";
import {
  ApiIntegration,
  ApiIntegrationEmailServers,
  ApiIntegrationEmailServersSave,
  ApiIntegrationEmployeeByType,
  ApiIntegrationEmployeesFilter,
  ApiIntegrationEmployeesType,
  ApiIntegrationGoogleCalendarServiceAccount,
  ApiIntegrationGoogleCalendarServiceAccountSave,
  ApiIntegrationGoogleWorkspace,
  ApiIntegrationGoogleWorkspaceSave,
  ApiIntegrationLoginUrl,
  ApiIntegrationSlack,
  ApiIntegrationSlackSave,
  ApiIntegrationType,
  ApiSlackChannel,
} from "@/types/integrations/integration.types.ts";
import { ApiPagedData } from "@skailer-com/uikit/core";

export const apiIntegrations = {
  uninstall: (id: number) => {
    return api.delete(`/api/v1/integrations/uninstall/${id}`);
  },

  uninstallForOrganization: (id: number) => {
    return api.delete<ApiOperationResult>(`/api/v1/integrations/uninstall/organization/${id}`);
  },

  emailMarkAsRead: (emailId: number | string) => {
    return api.put(`/api/v1/integrations/email/read/${emailId}`, {});
  },
  install: (integrationType: ApiIntegrationType) => {
    return api.get<ApiIntegrationLoginUrl>(`/api/v1/integrations/install/${integrationType}`);
  },

  add: (integrationType: ApiIntegrationType) => {
    return api.post<ApiIdObject>(`/api/v1/integrations/add/${integrationType}`, {});
  },

  getInstalled: () => {
    return api.get<ApiIntegration[]>(`/api/v1/integrations/installed`);
  },
  getInstalledForOrganization: () => {
    return api.get<ApiIntegration[]>(`/api/v1/integrations/installed/organization`);
  },

  getIntegrationGoogleServiceAccount: (id: number) => {
    return api.get<ApiIntegrationGoogleCalendarServiceAccount>(
      `/api/v1/integrations/manage/${id}/google-calendar-service-account`
    );
  },
  updateIntegrationGoogleServiceAccount: (
    id: number,
    payload: ApiIntegrationGoogleCalendarServiceAccountSave
  ) => {
    return api.put<ApiOperationResult>(
      `/api/v1/integrations/manage/${id}/google-calendar-service-account`,
      payload
    );
  },
  getIntegrationSlack: (id: number) => {
    return api.get<ApiIntegrationSlack>(`/api/v1/integrations/manage/${id}/slack`);
  },
  updateIntegrationSlack: (id: number, payload: ApiIntegrationSlackSave) => {
    return api.put<ApiOperationResult>(`/api/v1/integrations/manage/${id}/slack`, payload);
  },
  searchEmployees: <TType extends ApiIntegrationEmployeesType>(
    id: number,
    integrationType: TType,
    filter: ApiIntegrationEmployeesFilter
  ) => {
    return api.post<ApiPagedData<ApiIntegrationEmployeeByType[TType]>>(
      `/api/v1/integrations/manage/${id}/${integrationType}/employees/search`,
      filter
    );
  },
  exportEmployees: (
    id: number,
    integrationType: ApiIntegrationEmployeesType,
    filter: Omit<ApiIntegrationEmployeesFilter, "page_number">
  ) => {
    return api.post<BlobPart>(
      `/api/v1/integrations/manage/${id}/${integrationType}/employees/export`,
      filter,
      undefined,
      { responseType: "blob" }
    );
  },
  syncEmployees: (id: number, integrationType: ApiIntegrationEmployeesType) => {
    return api.post<ApiOperationResult>(
      `/api/v1/integrations/manage/${id}/${integrationType}/sync`,
      {}
    );
  },
  getSlackChannels: async function () {
    return api.get<ApiSlackChannel[]>(`/api/v1/integrations/slack-channels`);
  },
  addIntegrationEmailServers: (payload: ApiIntegrationEmailServersSave) => {
    return api.post<ApiIntegrationEmailServers>(`/api/v1/integrations/smtp/connect`, payload);
  },
  updateIntegrationEmailServers: (id: number, payload: ApiIntegrationEmailServersSave) => {
    return api.put<ApiIntegrationEmailServers>(`/api/v1/integrations/smtp/${id}`, payload);
  },
  getIntegrationEmailServers: (id: number) => {
    return api.get<ApiIntegrationEmailServers>(`/api/v1/integrations/smtp/${id}`);
  },
  getIntegrationGoogleWorkspace: (id: number) => {
    return api.get<ApiIntegrationGoogleWorkspace>(
      `/api/v1/integrations/manage/${id}/google-workspace`
    );
  },
  updateIntegrationGoogleWorkspace: (id: number, payload: ApiIntegrationGoogleWorkspaceSave) => {
    return api.put<ApiOperationResult>(
      `/api/v1/integrations/manage/${id}/google-workspace`,
      payload
    );
  },
};
